import AutoNumeric from "autonumeric";
import { type ComponentProps, useEffect, useRef, useState } from "react";

export interface NumericInputBaseProps extends Omit<
  ComponentProps<"input">,
  "onChange" | "onBlur" | "value" | "ref" | "autoFocus"
> {
  ref?: React.Ref<HTMLInputElement>;
  value?: string | number;
  autoNumericOptions?: Partial<AutoNumeric.Options>;
  onChange?: (value: string) => void;
  onBlur?: (value: string) => void;
}

const defaultConfig = AutoNumeric.getDefaultConfig();

const defaultAutoNumericOptions: AutoNumeric.Options = {
  allowDecimalPadding: false,
  emptyInputBehavior: "press",
  showWarnings: false,
  selectOnFocus: false,
  modifyValueOnWheel: false,
  modifyValueOnUpDownArrow: false,
  minimumValue: "0",
  maximumValue: defaultConfig.maximumValue,
  digitGroupSeparator: defaultConfig.digitGroupSeparator,
  decimalCharacter: defaultConfig.decimalCharacter,
  decimalPlaces: defaultConfig.decimalPlaces,
  currencySymbol: defaultConfig.currencySymbol,
  currencySymbolPlacement: defaultConfig.currencySymbolPlacement,
};

const getNumericString = (formatted: string) =>
  formatted.replace(/[^\d.-]/g, "");

export const NumericInputBase: React.FC<NumericInputBaseProps> = ({
  ref,
  value,
  className = "",
  placeholder = "0",
  disabled = false,
  autoNumericOptions = {},
  onChange,
  onBlur,
  ...props
}) => {
  const internalRef = useRef<HTMLInputElement>(null);
  const inputRef = (ref ?? internalRef) as React.RefObject<HTMLInputElement>;
  const autoNumericRef = useRef<AutoNumeric | null>(null);
  const onChangeRef = useRef(onChange);
  const onBlurRef = useRef(onBlur);
  const [isFocused, setIsFocused] = useState(false);

  const mergedOptions = { ...defaultAutoNumericOptions, ...autoNumericOptions };
  const mergedOptionsKey = JSON.stringify(mergedOptions);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onBlurRef.current = onBlur;
  }, [onBlur]);

  useEffect(() => {
    if (!inputRef.current) return;

    const currentInput = inputRef.current;

    autoNumericRef.current = new AutoNumeric(currentInput, mergedOptions);

    const handleAutoNumericFocus = () => {
      setIsFocused(true);
    };

    const handleAutoNumericBlur = () => {
      setIsFocused(false);
      onBlurRef.current?.(
        getNumericString(autoNumericRef.current?.getFormatted() ?? ""),
      );
    };
    const handleAutoNumericFormatted = () => {
      onChangeRef.current?.(
        getNumericString(autoNumericRef.current?.getFormatted() ?? ""),
      );
    };

    currentInput.addEventListener("focus", handleAutoNumericFocus);
    currentInput.addEventListener("blur", handleAutoNumericBlur);
    currentInput.addEventListener(
      "autoNumeric:formatted",
      handleAutoNumericFormatted,
    );

    return () => {
      currentInput.removeEventListener("focus", handleAutoNumericFocus);
      currentInput.removeEventListener("blur", handleAutoNumericBlur);
      currentInput.removeEventListener(
        "autoNumeric:formatted",
        handleAutoNumericFormatted,
      );

      autoNumericRef.current?.remove();
      autoNumericRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should not trigger a re-creation of the autoNumeric instance
  }, []);

  useEffect(() => {
    if (!autoNumericRef.current) return;

    autoNumericRef.current?.update(mergedOptions);

    // eslint-disable-next-line react-hooks/exhaustive-deps -- a stringified version of the merged options is used to trigger a re-update of the autoNumeric instance when the options change
  }, [mergedOptionsKey]);

  useEffect(() => {
    if (!autoNumericRef.current || value === undefined || isFocused) return;

    if (
      getNumericString(autoNumericRef.current?.getFormatted()) === String(value)
    )
      return;

    autoNumericRef.current.set(value);
  }, [value, isFocused]);

  return (
    <input
      type="text"
      ref={inputRef}
      disabled={disabled}
      inputMode="decimal"
      className={className}
      placeholder={placeholder}
      data-testid="numeric-input"
      {...props}
    />
  );
};
